// frontend/src/components/dashboard/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../../api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { fmt, fmtK, PayBadge, StatCard, Pager, CollapsibleCard } from '../common/CommonComponents';
import Skeleton, { SkeletonCard } from '../common/Skeleton';
import { getCachedDashboard, cacheDashboard } from '../../services/cacheService';
import DailyBriefToast from './DailyBriefToast';

// ── Quick Actions — one-tap launcher for the modules a manager actually
// uses day to day, so the dashboard isn't just a wall of numbers. Mirrors
// the same feature-flag gating App.js uses to build the sidebar (a card
// only appears if the module is actually reachable), and reuses the same
// window.location.hash navigation the rest of the app already relies on
// (see App.js's hashchange listener) rather than introducing a second
// routing mechanism.
function QuickActions({ user, pendingExpCount, pendingInvCount, lowStockCount }) {
  const f = user?.features || {
    hasOrders: true, hasRawMaterials: true, hasWalkinSales: true,
    hasAmbassadors: true, hasProfitAlloc: true, hasAssets: true,
  };

  const actions = [
    { key: "newsale",     icon: "🛒", label: "New Sale",       desc: "Ring up a walk-in sale",         show: f.hasWalkinSales },
    { key: "orders",      icon: "📦", label: "Orders",          desc: "Custom orders & production",     show: f.hasOrders },
    { key: "payments",    icon: "💳", label: "Payments",        desc: "Record & track collections",     show: f.hasOrders },
    { key: "pendingsales",icon: "✅", label: "Confirm Sales",   desc: "Review sales awaiting approval", show: f.hasWalkinSales },
    { key: "expenses",    icon: "🧾", label: "Expenses",        desc: "Log or approve spending",        show: true, badge: pendingExpCount },
    { key: "inventory",   icon: "🧱", label: "Raw Inventory",   desc: "Stock levels & restocks",        show: f.hasRawMaterials, badge: pendingInvCount || lowStockCount },
    { key: "marketplace", icon: "🏪", label: "Marketplace",     desc: "Storefront & listings",          show: true },
    { key: "ambassadors", icon: "🏅", label: "Ambassadors",     desc: "Referrals & payouts",             show: f.hasAmbassadors },
    { key: "capital",     icon: "💰", label: "Capital",         desc: "Loans, injections, repayments",  show: !user?.shopId },
    { key: "transfers",   icon: "🔁", label: "Transfers",       desc: "Central ↔ shop operating cash",  show: true },
    { key: "aianalysis",  icon: "🤖", label: "AI Analysis",     desc: "Ask about your numbers",         show: true },
    { key: "users",       icon: "👥", label: "Users",           desc: "Staff & permissions",             show: true },
    { key: "plan",        icon: "💎", label: "Plan & Billing",  desc: "Upgrade, renew, invoices",       show: true },
  ].filter(a => a.show);

  const go = (key) => { window.location.hash = key; };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:10 }}>
        <h3 style={{ fontSize:13, fontWeight:800, color:"var(--text)", textTransform:"uppercase", letterSpacing:".5px" }}>Quick Actions</h3>
      </div>
      <div className="quick-actions-grid">
        {actions.map(a => (
          <button key={a.key} className="quick-action-card" onClick={() => go(a.key)}
            title={a.desc} aria-label={`${a.label} — ${a.desc}`}>
            {!!a.badge && <span className="quick-action-badge">{a.badge > 9 ? "9+" : a.badge}</span>}
            <span className="quick-action-icon">{a.icon}</span>
            <span className="quick-action-label">{a.label}</span>
            <span className="quick-action-desc">{a.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ showAlert, user }) {
  const [data, setData] = useState(null);
  const [pendingExp, setPendingExp] = useState([]);
  const [pendingInv, setPendingInv] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moreKPIs, setMoreKPIs] = useState(false);
  const canApproveFinancialRequests = !!user?.isBusinessOwner || (user?.role === "manager" && !user?.shopId);
  const [dismissedSlow, setDismissedSlow] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dismissed_slow_v2") || "{}"); } catch { return {}; }
  });
  
  // ── FINANCIAL HEALTH STATE (NEW UPGRADE) ──
  const [financialHealth, setFinancialHealth] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Dashboard and pending approvals load in parallel.
      const [dash, expRes, invRes] = await Promise.all([
        api.getDashboard(),
        api.getExpenses({ status: "Pending", limit: 50 }).catch(() => ({ expenses: [] })),
        api.getInventory({ status: "Pending", limit: 50 }).catch(() => []),
      ]);
      setData(dash);
      setPendingExp(expRes.expenses || expRes || []);
      setPendingInv(Array.isArray(invRes) ? invRes : (invRes.items || []));
      
      // ── EXTRACT FINANCIAL HEALTH (NEW UPGRADE) ──
      if (dash.financialHealth) {
        setFinancialHealth(dash.financialHealth);
      }
      
      // ── CACHE FOR OFFLINE (NEW UPGRADE) ──
      cacheDashboard(dash);
      
    } catch(e) { 
      showAlert(e.message, "error"); 
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── All handlers remain exactly the same ──
  const handleApproveExp = async (id) => {
    try { await api.approveExpense(id, "approve"); showAlert("Expense approved"); load(); }
    catch(e) { showAlert(e.message, "error"); }
  };
  const handleRejectExp = async (id) => {
    const reason = window.prompt("Rejection reason (optional):");
    if (reason === null) return;
    try { await api.approveExpense(id, "reject"); showAlert("Expense rejected"); load(); }
    catch(e) { showAlert(e.message, "error"); }
  };
  const handleApproveInv = async (id) => {
    try { await api.approveInventory(id, "approve"); showAlert("Restock approved"); load(); }
    catch(e) { showAlert(e.message, "error"); }
  };
  const handleRejectInv = async (id) => {
    try { await api.approveInventory(id, "reject"); showAlert("Restock rejected"); load(); }
    catch(e) { showAlert(e.message, "error"); }
  };
  const dismissSlow = (name) => {
    const next = { ...dismissedSlow, [name]: new Date().toISOString() };
    setDismissedSlow(next);
    try { localStorage.setItem("dismissed_slow_v2", JSON.stringify(next)); } catch {}
  };
  const dismissAllSlow = () => {
    if (!window.confirm("Dismiss all slow-moving alerts? They reappear after 30 days.")) return;
    const next = {};
    (data?.slowMovingProducts || []).forEach(p => { next[p.name] = new Date().toISOString(); });
    const merged = { ...dismissedSlow, ...next };
    setDismissedSlow(merged);
    try { localStorage.setItem("dismissed_slow_v2", JSON.stringify(merged)); } catch {}
  };
  const isSlowVisible = (name, daysOnShelf) => {
    const ts = dismissedSlow[name];
    if (!ts) return true;
    const daysSince = Math.floor((Date.now() - new Date(ts)) / 86400000);
    if (daysOnShelf >= 30 && daysSince >= 30) return true;
    return false;
  };

  if (loading || !data) return (
    <div>
      <div className="stats-grid-4" style={{ marginBottom:16 }}>
        {[1,2,3,4].map(i=><div key={i} className="stat-card"><Skeleton height={10} width="55%" style={{marginBottom:8}}/><Skeleton height={22} width="75%"/></div>)}
      </div>
      <SkeletonCard rows={3}/><SkeletonCard rows={2}/>
    </div>
  );

  const d = data;

  // ── Exact field names from dashboard.js route ──────────────
  const liquidCash          = d.liquidCash             || 0;
  const totalRevenue        = d.totalRevenue            || 0;
  const totalBookedValue    = d.totalBookedValue        || 0;
  const totalCashReceived   = d.totalCashReceived       || 0;
  const customerFundsAwaitingRefund = d.customerFundsAwaitingRefund || 0;
  const monthRevenue        = d.monthRevenue            || 0;
  const monthCash           = d.monthCash               || 0;
  const monthRefundable     = d.monthRefundableCustomerFunds || 0;
  const grossProfit         = d.grossProfit             || 0;
  const netProfit           = d.netProfit               || 0;
  const totalExpenses       = d.totalExpenses           || 0;
  const outstanding         = d.outstanding             || 0;
  const combinedRevenue     = d.combinedRevenue         || 0;
  const combinedGrossProfit = d.combinedGrossProfit     || 0;
  const combinedNetProfit   = d.combinedNetProfit       || 0;
  const salesRevenue        = d.salesRevenue            || 0;
  const salesProfit         = d.salesProfit             || 0;
  const todaySalesRevenue   = d.todaySalesRevenue       || 0;
  const todaySalesCount     = d.todaySalesCount         || 0;
  const totalDiscounts      = d.totalDiscounts          || 0;
  const salesGrossMarginPct = d.salesGrossMarginPct     || 0;
  const capBorrowed         = d.capBorrowed             || 0;
  const capRepaid           = d.capRepaid               || 0;
  const capOutstanding      = d.capOutstanding          || 0;
  const ownerInjection      = d.ownerInjection          || 0;
  const ambassadorPayouts   = d.ambassadorPayouts       || 0;
  const inventorySpend      = d.inventorySpend          || 0;
  const rawInventoryValue   = d.rawInventoryValue       || 0;
  const finishedGoodsCostVal= d.finishedGoodsCostValue  || 0;
  const assetValue          = d.assetValue              || 0;
  const businessWorth       = d.businessWorth ?? d.totalNetWorth ?? (liquidCash + rawInventoryValue + finishedGoodsCostVal + assetValue);
  const recognizedLiabilities = d.recognizedLiabilities || 0;
  const netWorth            = d.netWorth ?? (businessWorth - recognizedLiabilities);
  const loanLiability       = d.loanLiability || 0;
  const ambassadorCommissionLiability = d.ambassadorCommissionLiability || 0;
  const moneyIn             = d.moneyIn                 || 0;
  const moneyOut            = d.moneyOut                || 0;
  const isShopScoped         = !!user?.shopId;
  // The liquidity total is ledger-based, so its visible lines must come
  // from that exact same source. Raw order/inventory totals hide paired
  // events such as a cancelled payment followed by its refund.
  const cashMovements = d.ledgerSummary?.cashMovementByType || {};
  const cashInFor = (type) => Number(cashMovements[type]?.in || 0);
  const cashOutFor = (type) => Number(cashMovements[type]?.out || 0);
  const liquidityInRows = [
    { label: "Customer Payments", value: cashInFor("CUSTOMER_PAYMENT") },
    { label: "Labour Released on Pending Cancellations", value: cashInFor("LABOUR_COST") },
    { label: "Internal Transfers In", value: cashInFor("INTERNAL_TRANSFER") },
    ...(!isShopScoped ? [{ label: "Capital Received", value: cashInFor("CAPITAL_INJECTION") }] : []),
  ];
  const liquidityOutRows = [
    { label: "Customer Refunds", value: cashOutFor("REFUND") + cashOutFor("CUSTOMER_REFUND_SETTLEMENT") },
    { label: "Payment Corrections", value: cashOutFor("CUSTOMER_PAYMENT") },
    { label: "Approved Expenses", value: cashOutFor("OVERHEAD_EXPENSE") },
    { label: "Ambassador Payouts", value: cashOutFor("AMBASSADOR_COMMISSION") },
    { label: "Internal Transfers Out", value: cashOutFor("INTERNAL_TRANSFER") },
    ...(!isShopScoped ? [{ label: "Capital Repaid", value: cashOutFor("CAPITAL_REPAYMENT") }] : []),
    { label: "Inventory Purchases", value: cashOutFor("INVENTORY_PURCHASE") },
    { label: "Labour Cost", value: cashOutFor("LABOUR_COST") },
    { label: "Asset Purchases", value: cashOutFor("ASSET_PURCHASE") },
    { label: "Owner Drawings", value: cashOutFor("OWNER_DRAWING") },
    { label: "Staff Bonuses", value: cashOutFor("PROFIT_DISTRIBUTION") },
  ];
  const shownMoneyIn = liquidityInRows.reduce((sum, row) => sum + row.value, 0);
  const shownMoneyOut = liquidityOutRows.reduce((sum, row) => sum + row.value, 0);
  // Future cash-bearing ledger types remain visible and reconcile until a
  // dedicated label is intentionally added above.
  if (moneyIn - shownMoneyIn > 0.005) liquidityInRows.push({ label: "Other Cash Received", value: moneyIn - shownMoneyIn });
  if (moneyOut - shownMoneyOut > 0.005) liquidityOutRows.push({ label: "Other Cash Paid", value: moneyOut - shownMoneyOut });
  const expenseRatioPct     = d.expenseRatioPct         || 0;
  const combinedGrossMargin = d.combinedGrossMarginPct  || 0;
  const totalStaffBonuses = d.totalStaffBonuses || 0;
  const totalOwnerDrawings = d.totalOwnerDrawings || 0;
  const totalReinvested = d.totalReinvested || 0;
  const monthSalesRevenue = d.monthSalesRevenue || 0;

  // ── Order breakdown: field is byStatus (not byPayment) ──────
  const byStatus    = d.byStatus     || {};
  const byProduction= d.byProduction || {};
  const paidCount    = byStatus["Paid"]           || 0;
  const partialCount = byStatus["Partially Paid"] || 0;
  const unpaidCount  = byStatus["Unpaid"]         || 0;

  const top5 = d.top5 || [];

  const displayRevenue = combinedRevenue || totalRevenue;
  const grossMarginPct = combinedGrossMargin || (displayRevenue>0?((combinedGrossProfit/displayRevenue)*100).toFixed(1):0);
  const netMarginPct   = displayRevenue>0?((combinedNetProfit/displayRevenue)*100).toFixed(1):0;
  const collectibleCash=Math.max((d.totalCashReceived||0)-customerFundsAwaitingRefund,0)+salesRevenue;
  const collectionRate = displayRevenue>0?Math.min(100,(collectibleCash/displayRevenue*100)).toFixed(1):0;

  const avgMonthlyBurn  = totalExpenses / Math.max(1, 6);
  const cashCoverageDays = avgMonthlyBurn>0 ? Math.round((liquidCash/avgMonthlyBurn)*30) : null;

  const expChartData = Object.entries(d.expByCategory||{})
    .filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]).slice(0,5)
    .map(([k,v],i)=>({ label:k.split("/")[0].slice(0,13), value:v,
      color:["#10B981","#3B82F6","#F59E0B","#8B5CF6","#EF4444"][i] }));

  const prodData = [
    { label:"Pending",  value:byProduction["Pending"]||0,        color:"#94A3B8" },
    { label:"In Prod",  value:byProduction["In Production"]||0,  color:"#3B82F6" },
    { label:"Ready",    value:byProduction["Ready"]||0,           color:"#F59E0B" },
    { label:"Delivered",value:byProduction["Delivered"]||0,       color:"#10B981" },
  ];

  const healthData = {
    combinedGrossMarginPct: grossMarginPct,
    expenseRatioPct: expenseRatioPct,
    liquidCash, combinedRevenue: displayRevenue,
    totalCashReceived: d.totalCashReceived||0, salesRevenue,
  };

  const recentOrders   = d.recentOrders          || [];
  const lowStock       = d.lowStockProducts       || [];
  const visibleSlow    = (d.slowMovingProducts||[]).filter(p=>isSlowVisible(p.name,p.daysOnShelf));
  const totalPendingCount = pendingExp.length + pendingInv.filter(i=>i.status==="Pending").length;

  const KPIItem = ({ label, value, color="var(--text)", sub }) => (
    <div style={{ padding:"9px 14px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
      <span style={{ fontSize:12, color:"var(--text-2)", fontWeight:500 }}>{label}</span>
      <div style={{ textAlign:"right" }}>
        <span style={{ fontSize:13, fontWeight:800, color }}>{value}</span>
        {sub && <div style={{ fontSize:10, color:"var(--text-3)" }}>{sub}</div>}
      </div>
    </div>
  );

  // ── HealthScoreWidget (inline) ──
  const HealthScoreWidget = ({ data }) => {
    if (!data) return null;
    const metrics = [];
    const gm = parseFloat(data.combinedGrossMarginPct || 0);
    metrics.push({ label: "Gross Margin", value: Math.min(gm, 100), target: 30, color: "#10B981" });
    const er = parseFloat(data.expenseRatioPct || 0);
    const erScore = Math.max(0, 100 - er * 1.5);
    metrics.push({ label: "Expense Ratio", value: erScore, target: 50, color: "#3B82F6" });
    const liq = data.liquidCash || 0;
    const rev = data.combinedRevenue || 1;
    const cashScore = Math.min(Math.max((liq / rev) * 100, 0), 100);
    metrics.push({ label: "Cash Position", value: cashScore, target: 20, color: "#F59E0B" });
    const collRate = rev > 0 ? Math.min(((data.totalCashReceived || 0) + (data.salesRevenue || 0)) / rev * 100, 100) : 50;
    metrics.push({ label: "Collection Rate", value: collRate, target: 70, color: "#8B5CF6" });
    const score = Math.round(metrics.reduce((s, m) => s + m.value, 0) / metrics.length);
    const scoreColor = score >= 70 ? "#10B981" : score >= 45 ? "#F59E0B" : "#EF4444";
    const scoreLabel = score >= 70 ? "Healthy" : score >= 45 ? "Moderate" : "Needs Attention";
    const r = 36, circ = 2 * Math.PI * r;
    const dashArr = `${(score / 100) * circ} ${circ}`;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flex: "none" }}>
          <svg width="88" height="88" viewBox="0 0 88 88">
            <circle cx="44" cy="44" r={r} fill="none" stroke="var(--border)" strokeWidth="6" />
            <circle cx="44" cy="44" r={r} fill="none" stroke={scoreColor} strokeWidth="6" strokeDasharray={dashArr} strokeDashoffset={circ * 0.25} strokeLinecap="round" transform="rotate(-90 44 44)" style={{ transition: "stroke-dasharray .8s ease" }} />
            <text x="44" y="40" textAnchor="middle" fontSize="18" fontWeight="800" fill={scoreColor} fontFamily="'Segoe UI',sans-serif">{score}</text>
            <text x="44" y="54" textAnchor="middle" fontSize="9" fill="var(--text-3)" fontFamily="'Segoe UI',sans-serif">/ 100</text>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: scoreColor, marginBottom: 2 }}>{scoreLabel}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>Business health score</div>
          {metrics.map(m => (
            <div key={m.label} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", width: 90, flexShrink: 0 }}>{m.label}</div>
              <div style={{ flex: 1, height: 4, background: "var(--border)", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: `${m.value}%`, height: "100%", borderRadius: 2, background: m.color, transition: "width .8s ease" }} />
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: m.value >= m.target ? "#10B981" : "#F59E0B", width: 32, textAlign: "right" }}>{Math.round(m.value)}%</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── CustomDonutChart (inline) ──
  const CustomDonutChart = ({ data = [], size = 120, thickness = 20 }) => {
    const total = data.reduce((s, d) => s + (d.value || 0), 0);
    if (!total) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)", fontSize: 11 }}>No data</div>;
    const cx = size / 2, cy = size / 2, r = (size - thickness) / 2;
    const circ = 2 * Math.PI * r;
    let offset = 0;
    const colors = ["#10B981", "#3B82F6", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4", "#F97316", "#EC4899"];
    const slices = data.map((d, i) => {
      const pct = d.value / total;
      const dash = pct * circ;
      const gap = circ - dash;
      const slice = (
        <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={d.color || colors[i % colors.length]} strokeWidth={thickness} strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-offset * circ} transform={`rotate(-90 ${cx} ${cy})`} style={{ transition: "stroke-dasharray .6s ease" }} />
      );
      offset += pct;
      return slice;
    });
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth={thickness} />
        {slices}
      </svg>
    );
  };

  return (
    <div>

      {/* ═══ FINANCIAL HEALTH WARNING (NEW UPGRADE) ═══ */}
      {financialHealth?.drawing?.isOverdrawn && (
        <div style={{ 
          background: "#fee2e2", 
          border: "2px solid #ef4444", 
          borderRadius: 12, 
          padding: 16,
          marginBottom: 16
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <span style={{ fontSize: 24 }}>🚨</span>
            <div>
              <strong style={{ color: "#dc2626" }}>Critical Alert: Business Overdrawn</strong>
              <p style={{ fontSize: 13, color: "#991b1b", marginTop: 4 }}>
                Owner drawings (₦{financialHealth.drawing.totalDrawings.toLocaleString()}) have exceeded 
                total net profit (₦{financialHealth.drawing.totalNetProfit.toLocaleString()}) 
                by <strong>₦{financialHealth.drawing.overdrawnAmount.toLocaleString()}</strong>.<br />
                This is draining business capital. <strong>Immediate action required.</strong>
              </p>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button className="btn-sm btn-danger" onClick={() => window.location.hash = "allocation"}>
                  View Profit Allocation
                </button>
                <button className="btn-sm btn-secondary" onClick={() => setFinancialHealth({ ...financialHealth, drawing: { ...financialHealth.drawing, isOverdrawn: false } })}>
                  Dismiss Warning
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ QUICK ACTIONS — module launcher ═══════════════════════ */}
      <QuickActions user={user} pendingExpCount={pendingExp.length}
        pendingInvCount={pendingInv.filter(i=>i.status==="Pending").length}
        lowStockCount={lowStock.length} />

      {/* ═══ DAILY AI BRIEF — first sign-in of the day toast ═══════ */}
      <DailyBriefToast user={user} dashboardData={d}
        pendingExpenses={pendingExp.length}
        pendingRestocks={pendingInv.filter(i=>i.status==="Pending").length}
        lowStockCount={lowStock.length} />

      {/* ═══ HERO ROW ═══════════════════════════════════════════ */}
      <div className="hero-grid" style={{ marginBottom:16 }}>
        {/* Liquid Cash */}
        <div style={{ background:"linear-gradient(135deg,#0B1120 0%,#1E293B 100%)", borderRadius:16, padding:20, color:"#fff", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:-40, right:-40, width:120, height:120, background:"radial-gradient(circle,rgba(16,185,129,.3),transparent 70%)", pointerEvents:"none" }}/>
          <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,.4)", textTransform:"uppercase", letterSpacing:".6px", marginBottom:6 }}>Liquid Cash</div>
          <div style={{ fontSize:26, fontWeight:900, letterSpacing:"-1px", marginBottom:4 }}>{fmtK(liquidCash)}</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,.4)", marginBottom:10 }}>What should physically be in the account</div>
          {cashCoverageDays !== null && (
            <div title={`Your liquid cash (${fmtK(liquidCash)}) divided by your average monthly expenses (${fmtK(avgMonthlyBurn)}) gives roughly ${cashCoverageDays} days before cash runs out — assuming no new income.`}
              style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(16,185,129,.15)", borderRadius:6, padding:"5px 10px", cursor:"help" }}>
              <span>⏱</span>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:"#10B981" }}>~{cashCoverageDays} day{cashCoverageDays!==1?"s":""} of cash coverage</div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,.3)" }}>at avg monthly burn · hover for details</div>
              </div>
            </div>
          )}
        </div>

        {/* This Month */}
        <div style={{ background:"linear-gradient(135deg,#10B981,#059669)", borderRadius:16, padding:20, color:"#fff", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:-40, right:-40, width:120, height:120, background:"radial-gradient(circle,rgba(255,255,255,.15),transparent 70%)", pointerEvents:"none" }}/>
          <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,.7)", textTransform:"uppercase", letterSpacing:".6px", marginBottom:6 }}>This Month</div>
          <div style={{ fontSize:26, fontWeight:900, letterSpacing:"-1px", marginBottom:4 }}>{fmtK(monthRevenue + monthSalesRevenue)}</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,.7)", marginBottom:10 }}>
            {fmtK(monthCash)} customer cash held/received · {fmtK(monthRefundable)} refundable · {grossMarginPct}% margin
            {monthSalesRevenue > 0 && ` · Walk-in: ${fmtK(monthSalesRevenue)}`}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <div style={{ background:"rgba(0,0,0,.15)", borderRadius:6, padding:"6px 10px", flex:1, textAlign:"center" }}>
              <div style={{ fontSize:9, color:"rgba(255,255,255,.6)", textTransform:"uppercase", marginBottom:2 }}>Earned Orders</div>
              <div style={{ fontSize:13, fontWeight:800 }}>{fmtK(monthRevenue)}</div>
            </div>
            <div style={{ background:"rgba(0,0,0,.15)", borderRadius:6, padding:"6px 10px", flex:1, textAlign:"center" }}>
              <div style={{ fontSize:9, color:"rgba(255,255,255,.6)", textTransform:"uppercase", marginBottom:2 }}>Walk-in</div>
              <div style={{ fontSize:13, fontWeight:800 }}>{fmtK(monthSalesRevenue)}</div>
            </div>
          </div>
        </div>

        {/* Business Worth */}
        <div style={{ background:"var(--surface)", borderRadius:16, padding:20, border:"1px solid var(--border)", boxShadow:"var(--sh-sm)" }}>
          <div style={{ fontSize:10, fontWeight:700, color:"var(--text-3)", textTransform:"uppercase", letterSpacing:".6px", marginBottom:6 }}>Business Worth</div>
          <div style={{ fontSize:26, fontWeight:900, letterSpacing:"-1px", marginBottom:4, color:"var(--text)" }}>{fmtK(businessWorth)}</div>
          <div style={{ fontSize:11, color:"var(--text-3)", marginBottom:10 }}>Net Worth {fmtK(netWorth)} · Liabilities {fmtK(recognizedLiabilities)}</div>
          {[
            { label:"💵 Liquid Cash",   value:liquidCash,        color:"var(--accent)" },
            { label:"🧱 Raw Stock",      value:rawInventoryValue, color:"var(--blue)"   },
            { label:"🛍 Finished Goods", value:finishedGoodsCostVal, color:"var(--gold)" },
            { label:"🏢 Recorded Assets", value:assetValue, color:"var(--purple, #8B5CF6)" },
          ].map(r=>(
            <div key={r.label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
              <span style={{ fontSize:11, color:"var(--text-3)" }}>{r.label}</span>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ width:`${businessWorth>0?Math.round(r.value/businessWorth*60):0}px`, minWidth:2, height:4, borderRadius:2, background:r.color, transition:"width .5s" }}/>
                <span style={{ fontSize:11, fontWeight:800, color:r.color }}>{fmtK(r.value)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ HEALTH + CHARTS ROW ════════════════════════════════ */}
      <div className="charts-grid" style={{ marginBottom:16 }}>
        <div className="card" style={{ marginBottom:0 }}>
          <div className="card-header">
            <div className="card-title">Business Health Score</div>
            <span style={{ fontSize:11, color:"var(--text-3)" }}>All-time</span>
          </div>
          <HealthScoreWidget data={healthData}/>
        </div>

        {expChartData.length>0 ? (
          <div className="card" style={{ marginBottom:0 }}>
            <div className="card-title" style={{ marginBottom:12 }}>Expense Breakdown</div>
            <div style={{ display:"flex", gap:10, alignItems:"center" }}>
              <div style={{ flexShrink:0 }}><CustomDonutChart data={expChartData} size={88} thickness={14}/></div>
              <div style={{ flex:1, overflow:"hidden" }}>
                {expChartData.map((item,i)=>(
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:5, marginBottom:5 }}>
                    <div style={{ width:7, height:7, borderRadius:2, background:item.color, flexShrink:0 }}/>
                    <div style={{ fontSize:10, color:"var(--text-2)", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.label}</div>
                    <div style={{ fontSize:10, fontWeight:700, color:"var(--text)", flexShrink:0 }}>{fmtK(item.value)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="card" style={{ marginBottom:0, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:6, color:"var(--text-3)", fontSize:12 }}>
            <div style={{ fontSize:24 }}>🧾</div>No expenses recorded yet
          </div>
        )}

        <div className="card" style={{ marginBottom:0 }}>
          <div className="card-title" style={{ marginBottom:10 }}>Order Pipeline</div>
          {prodData.every(x=>x.value===0) ? (
            <div style={{ textAlign:"center", padding:"16px 0", color:"var(--text-3)", fontSize:12 }}>No orders yet</div>
          ) : (
            <BarChart data={prodData} height={76}/>
          )}
          <div style={{ marginTop:10, display:"flex", gap:6 }}>
            {[
              { label:"Paid",    count:paidCount,    color:"#166534", bg:"#F0FDF4" },
              { label:"Partial", count:partialCount, color:"#92400E", bg:"#FFFBEB" },
              { label:"Unpaid",  count:unpaidCount,  color:"#BE123C", bg:"#FFF1F2" },
            ].map(s=>(
              <div key={s.label} style={{ flex:1, textAlign:"center", background:s.bg, borderRadius:6, padding:"6px 4px" }}>
                <div style={{ fontSize:17, fontWeight:900, color:s.color }}>{s.count}</div>
                <div style={{ fontSize:9, fontWeight:700, color:s.color, textTransform:"uppercase", letterSpacing:".4px" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ COMPACT KPI TABLE ══════════════════════════════════ */}
      <div className="kpi-grid" style={{ marginBottom:16 }}>
        <div className="card" style={{ marginBottom:0, padding:0, overflow:"hidden" }}>
          <div style={{ padding:"11px 14px", borderBottom:"1px solid var(--border)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontWeight:700, fontSize:13 }}>📊 Revenue & Profit</span>
            <button className="kpi-toggle" onClick={()=>setMoreKPIs(o=>!o)} style={{ fontSize:10 }}>{moreKPIs?"▲ Less":"▼ More"}</button>
          </div>
          <KPIItem label="Earned Revenue" value={fmtK(combinedRevenue)} color="var(--accent)" sub={`Cash received ${fmtK(totalCashReceived + salesRevenue)} · refundable portion ${fmtK(customerFundsAwaitingRefund)}`} />
          <KPIItem label="Booked Order Value" value={fmtK(totalBookedValue)} color="var(--text-2)" sub="Active order value, including unpaid balances" />
          <KPIItem label="Refundable / Customer-Held Funds" value={fmtK(customerFundsAwaitingRefund)} color="var(--gold)" sub="Available for cash refund or same-customer credit; excluded from earned revenue and profit" />
          <KPIItem label="Gross Profit"      value={fmtK(combinedGrossProfit)} color="var(--accent)"  sub={`${grossMarginPct}% margin`} />
          <KPIItem label="Net Profit"        value={fmtK(combinedNetProfit)}   color={combinedNetProfit>=0?"var(--accent)":"var(--red)"} sub={`${netMarginPct}% net`} />
          <KPIItem label="Outstanding"       value={fmtK(outstanding)}          color="var(--gold)" />
          
          <KPIItem label="Collection Rate"   value={`${collectionRate}%`}       color={parseFloat(collectionRate)>=70?"var(--accent)":"var(--gold)"} />
          {moreKPIs && <>
            <KPIItem label="Order Earned Revenue" value={fmtK(totalRevenue)} color="var(--text-2)" />
            <KPIItem label="Walk-in Collected Revenue" value={fmtK(salesRevenue)} color="var(--text-2)" />
            <KPIItem label="Walk-in Profit"  value={fmtK(salesProfit)}          color="var(--accent)" sub={`${salesGrossMarginPct}% margin`} />
            <KPIItem label="Today's Sales"   value={fmtK(todaySalesRevenue)}    color="var(--blue)"   sub={`${todaySalesCount} transaction${todaySalesCount!==1?"s":""}`} />
            <KPIItem label="Total Discounts" value={fmtK(totalDiscounts)}        color="var(--red)" />
          </>}
        </div>

        <div className="card" style={{ marginBottom:0, padding:0, overflow:"hidden" }}>
          <div style={{ padding:"11px 14px", borderBottom:"1px solid var(--border)" }}>
            <span style={{ fontWeight:700, fontSize:13 }}>{isShopScoped ? "💰 Shop Expenses & Stock" : "💰 Expenses & Capital"}</span>
          </div>
          <KPIItem label="Total Expenses"     value={fmtK(totalExpenses)}    color="var(--red)"    sub={`${expenseRatioPct}% of revenue`} />
          <KPIItem label="Raw Inventory"      value={fmtK(rawInventoryValue)} color="var(--blue)" />
          <KPIItem label="Finished Goods"     value={fmtK(finishedGoodsCostVal)} color="var(--gold)" />
          {!isShopScoped && <>
            <KPIItem label="Capital Borrowed"   value={fmtK(capBorrowed)}       color="var(--gold)" />
            <KPIItem label="Capital Outstanding"value={fmtK(capOutstanding)}    color="var(--red)" />
            <KPIItem label="Recognized Liabilities" value={fmtK(recognizedLiabilities)} color="var(--red)" sub={`Loans ${fmtK(loanLiability)} · customer-held ${fmtK(customerFundsAwaitingRefund)} · ambassador ${fmtK(ambassadorCommissionLiability)}`} />
            <KPIItem label="Net Worth" value={fmtK(netWorth)} color={netWorth>=0?"var(--accent)":"var(--red)"} sub="Business Worth minus recognized liabilities" />
          </>}
          {moreKPIs && <>
            {!isShopScoped && <>
              <KPIItem label="Capital Repaid"     value={fmtK(capRepaid)}         color="var(--accent)" />
              <KPIItem label="Owner Injection"    value={fmtK(ownerInjection)}    color="var(--blue)" />
            </>}
            <KPIItem label="Inventory Spend"    value={fmtK(inventorySpend)}    color="var(--gold)" />
            <KPIItem label="Ambassador Payouts" value={fmtK(ambassadorPayouts)} color="var(--violet,#8B5CF6)" />
          </>}
        </div>
      </div>

      {/* ═══ WALK-IN SALES QUICK VIEW ═══════════════════════════ */}
      {(salesRevenue > 0 || todaySalesCount > 0) && (
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div className="card-title">🛒 Walk-in Sales</div>
            {todaySalesCount>0 && <span style={{ background:"var(--accent-lite)", color:"var(--accent)", fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:999 }}>{todaySalesCount} sale{todaySalesCount!==1?"s":""} today</span>}
          </div>
          <div className="walkin-grid">
            {[
              { label:"All-time Revenue", value:fmtK(salesRevenue),      color:"var(--accent)" },
              { label:"All-time Profit",  value:fmtK(salesProfit),       color:"var(--accent)", sub:`${salesGrossMarginPct}% margin` },
              { label:"Today's Revenue",  value:fmtK(todaySalesRevenue), color:"var(--blue)"   },
              { label:"Discounts Given",  value:fmtK(totalDiscounts),    color:"var(--gold)"   },
            ].map(k=>(
              <div key={k.label} style={{ background:"var(--surface2)", borderRadius:8, padding:"10px 12px", border:"1px solid var(--border)" }}>
                <div style={{ fontSize:10, color:"var(--text-3)", fontWeight:600, marginBottom:4 }}>{k.label}</div>
                <div style={{ fontSize:14, fontWeight:800, color:k.color }}>{k.value}</div>
                {k.sub && <div style={{ fontSize:10, color:"var(--text-3)", marginTop:2 }}>{k.sub}</div>}
              </div>
            ))}
          </div>
          {/*{lowStock.length>0 && (
            <div className="alert alert-warning" style={{ marginTop:0, marginBottom:0, fontSize:0 }}>
              ⚠️ <strong>Low Stock:</strong> {lowStock.map(p=>`${p.name} (${p.quantity} left)`).join(" · ")} 
            </div>
          )}  */}
        </div>
      )}

      {/* ═══ SLOW-MOVING STOCK ══════════════════════════════════ */}
      {visibleSlow.length>0 && (
        <div className="card" style={{ marginBottom:16, borderLeft:"4px solid #8B5CF6" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, flexWrap:"wrap", gap:8 }}>
            <div>
              <div style={{ fontWeight:700, fontSize:14 }}>🐌 Slow-Moving Display Stock</div>
              <div style={{ fontSize:11, color:"var(--text-3)", marginTop:2 }}>On shelf 30+ days — consider promotions or price cuts</div>
            </div>
            <button className="btn-sm btn-secondary" onClick={dismissAllSlow}>Dismiss All</button>
          </div>
          {visibleSlow.map(p=>(
            <div key={p.name} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderTop:"1px solid var(--border)", fontSize:12, flexWrap:"wrap", gap:8 }}>
              <span style={{ fontWeight:600 }}>{p.name}</span>
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                <span style={{ color:"#8B5CF6", fontWeight:600 }}>{p.quantity} units · {p.daysOnShelf} days on shelf</span>
                <button onClick={()=>dismissSlow(p.name)} style={{ background:"none", border:"none", color:"var(--text-3)", cursor:"pointer", fontSize:14 }} title="Dismiss for 30 days">✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ PENDING APPROVALS ══════════════════════════════════ */}
      {totalPendingCount>0 && (
        <CollapsibleCard title={`⚠️ ${canApproveFinancialRequests ? "Pending Approvals" : "Pending Requests"} (${totalPendingCount})`}
          headerRight={<span style={{ fontSize:10, background:"var(--red-lite)", color:"var(--red)", fontWeight:700, padding:"2px 8px", borderRadius:4 }}>{canApproveFinancialRequests ? "Action Needed" : "Awaiting Central"}</span>}>
          {pendingExp.length>0 && (
            <><p style={{ fontSize:11, fontWeight:700, color:"var(--text-3)", textTransform:"uppercase", marginBottom:8 }}>Expenses</p>
            {pendingExp.map(e=>(
              <div key={e._id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid var(--border)", gap:10, flexWrap:"wrap" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{e.category} — {fmt(e.amount)}</div>
                  <div style={{ fontSize:11, color:"var(--text-3)" }}>{e.description} · by {e.loggedBy}</div>
                </div>
                {canApproveFinancialRequests && <div style={{ display:"flex", gap:6 }}>
                  <button className="btn-sm btn-approve" onClick={()=>handleApproveExp(e.expenseId || e._id)}>✓ Approve</button>
                  <button className="btn-sm btn-reject"  onClick={()=>handleRejectExp(e.expenseId || e._id)}>✕ Reject</button>
                </div>}
              </div>
            ))}</>
          )}
          {pendingInv.filter(i=>i.status==="Pending").length>0 && (
            <><p style={{ fontSize:11, fontWeight:700, color:"var(--text-3)", textTransform:"uppercase", marginTop:pendingExp.length>0?12:0, marginBottom:8 }}>Inventory Restocks</p>
            {pendingInv.filter(i=>i.status==="Pending").map(inv=>(
              <div key={inv._id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid var(--border)", gap:10, flexWrap:"wrap" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{inv.item||inv.material} — {inv.quantity} units @ {fmt(inv.unitCost)}</div>
                  <div style={{ fontSize:11, color:"var(--text-3)" }}>Total: {fmt(inv.totalCost)} · by {inv.loggedBy}</div>
                </div>
                {canApproveFinancialRequests && <div style={{ display:"flex", gap:6 }}>
                  <button className="btn-sm btn-approve" onClick={()=>handleApproveInv(inv.restockId || inv._id)}>✓ Approve</button>
                  <button className="btn-sm btn-reject"  onClick={()=>handleRejectInv(inv.restockId || inv._id)}>✕ Reject</button>
                </div>}
              </div>
            ))}</>
          )}
        </CollapsibleCard>
      )}

      {/* ═══ TOP 5 CLIENTS + RECENT ORDERS ═════════════════════ */}
      <div className="split-grid" style={{ marginBottom:16 }}>
        {top5.length>0 && (
          <div className="card" style={{ marginBottom:0 }}>
            <h2>🏆 Top 5 Clients</h2>
            {top5.map((c,i)=>(
              <div key={i} className="rank-row">
                <div className={`rank-num ${i===0?"gold":i===1?"silver":i===2?"bronze":""}`}>{i+1}</div>
                <div style={{ flex:1, overflow:"hidden" }}>
                  <div style={{ fontWeight:600, fontSize:12, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{c.client}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:12, fontWeight:800, color:"var(--accent)" }}>{fmtK(c.revenue)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {recentOrders.length>0 && (
          <div className="card" style={{ marginBottom:0 }}>
            <h2>📦 Recent Orders</h2>
            <div className="table-wrap">
              <table>
                <thead><tr><th>ID</th><th>Client</th><th>Item</th><th>Total</th><th>Paid</th><th>Prod.</th><th>Payment</th></tr></thead>
                <tbody>
                  {recentOrders.map(o=>(
                    <tr key={o._id}>
                      <td style={{ fontFamily:"monospace", fontSize:11 }}>{o.orderId}</td>
                      <td style={{ fontWeight:600 }}>{o.client}</td>
                      <td style={{ color:"var(--text-2)", fontSize:11 }}>{o.item}</td>
                      <td style={{ fontWeight:700 }}>{fmtK(o.totalSales)}</td>
                      <td style={{ fontWeight:600, color:"var(--accent)" }}>{fmtK(o.totalPaid)}</td>
                      <td><PayBadge status={o.productionStatus}/></td>
                      <td><PayBadge status={o.paymentStatus}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ═══ LIQUIDITY BREAKDOWN ════════════════════════════════ */}
      <CollapsibleCard title="💵 Liquidity Breakdown" defaultOpen={false}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
          <div style={{ background:"var(--surface2)", borderRadius:8, padding:12, border:"1px solid var(--border)" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"var(--accent)", textTransform:"uppercase", marginBottom:8 }}>Money In</div>
            {liquidityInRows.map(r=>(
              <div key={r.label} style={{ display:"flex", justifyContent:"space-between", marginBottom:5, fontSize:12 }}>
                <span style={{ color:"var(--text-2)" }}>{r.label}</span>
                <span style={{ fontWeight:700 }}>{fmt(r.value)}</span>
              </div>
            ))}
            <div style={{ borderTop:"1px solid var(--border)", paddingTop:8, marginTop:4, display:"flex", justifyContent:"space-between", fontWeight:800, fontSize:13, color:"var(--accent)" }}>
              <span>Total In</span><span>{fmt(moneyIn)}</span>
            </div>
          </div>
          <div style={{ background:"var(--surface2)", borderRadius:8, padding:12, border:"1px solid var(--border)" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"var(--red)", textTransform:"uppercase", marginBottom:8 }}>Money Out</div>
            {liquidityOutRows.map(r=>(
              <div key={r.label} style={{ display:"flex", justifyContent:"space-between", marginBottom:5, fontSize:12 }}>
                <span style={{ color:"var(--text-2)" }}>{r.label}</span>
                <span style={{ fontWeight:700 }}>{fmt(r.value)}</span>
              </div>
            ))}
            <div style={{ borderTop:"1px solid var(--border)", paddingTop:8, marginTop:4, display:"flex", justifyContent:"space-between", fontWeight:800, fontSize:13, color:"var(--red)" }}>
              <span>Total Out</span><span>{fmt(moneyOut)}</span>
            </div>
          </div>
        </div>
        <div style={{ background:"linear-gradient(135deg,#0B1120,#1E293B)", borderRadius:8, padding:14, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ color:"rgba(255,255,255,.55)", fontSize:13 }}>Liquid Cash at Hand (In − Out)</div>
          <div style={{ fontSize:22, fontWeight:900, color:"#10B981" }}>{fmt(liquidCash)}</div>
        </div>
        
        <div className="alert alert-info" style={{ marginTop:10, marginBottom:0, fontSize:11 }}>
          If this figure does not match your physical account balance, report the discrepancy to your manager immediately.
        </div>
      </CollapsibleCard>

    </div>
  );
}

export default Dashboard;
